import { Box, Typography } from "@mui/material";
import { styled, keyframes } from "@mui/system";
import { useEffect, useRef, useState } from "react";

// Keyframe animations
const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.15); opacity: 0.9; }
`;

const rotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-15px); }
`;

const gradient = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Fullscreen overlay
const Overlay = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(58, 50, 50, 0.21)",
  backdropFilter: "blur(4px)",
  zIndex: 9999,
});

// Loader container
const LoaderContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "40px",
  borderRadius: "24px",
  background: "linear-gradient(-45deg, #f5f7fa, #e4f0f9, #f0f5ff, #e6f0ff)",
  backgroundSize: "400% 400%",
  animation: `${gradient} 8s ease infinite`,
  boxShadow: "0 15px 30px rgba(0, 0, 0, 0.1)",
  border: "1px solid rgba(255, 255, 255, 0.3)",
});

const DollarIcon = styled(Box)({
  width: "80px",
  height: "80px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(135deg, #2ecc71, #27ae60)",
  color: "white",
  fontSize: "36px",
  fontWeight: "bold",
  marginBottom: "30px",
  boxShadow: "0 10px 20px rgba(46, 204, 113, 0.3)",
  animation: `${pulse} 2s ease infinite, ${float} 3s ease infinite`,
  position: "relative",
  "&:after": {
    content: '""',
    position: "absolute",
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    border: "2px solid rgba(255, 255, 255, 0.4)",
    animation: `${rotate} 3s linear infinite`,
  },
});

const ProgressTrack = styled(Box)({
  width: "200px",
  height: "8px",
  backgroundColor: "rgba(0, 0, 0, 0.05)",
  borderRadius: "4px",
  margin: "20px 0",
  overflow: "hidden",
});

const ProgressBar = styled(Box)({
  height: "100%",
  background: "linear-gradient(90deg, #2ecc71, #3498db)",
  borderRadius: "4px",
  transition: "width 0.3s ease",
});

const StatusText = styled(Typography)({
  marginTop: "15px",
  color: "#2c3e50",
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "uppercase",
  letterSpacing: "1px",
});

const PercentageText = styled(Typography)({
  color: "#3498db",
  fontWeight: 700,
  fontSize: "14px",
  marginLeft: "10px",
});

const Loader = ({ loading = false, status = "" }) => {
  const [progress, setProgress] = useState(0);
  const [show, setShow] = useState(false);
  const intervalRef = useRef();

  // Animate progress when loading starts
  useEffect(() => {
    if (loading) {
      setShow(true);
      setProgress(0);
      let prog = 0;
      intervalRef.current = setInterval(() => {
        prog += Math.random() * 10;
        if (prog < 90) setProgress(prog);
        else clearInterval(intervalRef.current);
      }, 120);
    }
    return () => clearInterval(intervalRef.current);
  }, [loading]);

  // When loading ends, fill to 100% and hide loader after 0.5s
  useEffect(() => {
    if (!loading && show) {
      setProgress(100);
      const timeout = setTimeout(() => {
        setShow(false);
        setProgress(0);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [loading, show]);

  const financialPhrases = [
    "Securing assets",
    "Processing transaction",
    "Analyzing markets",
    "Optimizing portfolio",
    "Verifying details",
    "Finalizing deal",
    "Updating records",
    "Calculating returns",
  ];

  const randomStatus =
    financialPhrases[Math.floor(Math.random() * financialPhrases.length)];

  return (
    <Overlay>
      <LoaderContainer>
        <DollarIcon>$</DollarIcon>

        <ProgressTrack>
          <ProgressBar style={{ width: `${progress}%` }} />
        </ProgressTrack>

        <Box display="flex" alignItems="center">
          <StatusText>{status || randomStatus}</StatusText>
          <PercentageText>{Math.round(progress)}%</PercentageText>
        </Box>
      </LoaderContainer>
    </Overlay>
  );
};

export default Loader;
